import { getAPI, postAPI, postMultipartAPI } from "../../../api/Networking";
import {
  GetPodOrdersParams,
  PodOrderBatchInventoryAllocateBody,
  PodOrderSetWarehouseBody,
  PodOrderSetPrintingTypeBody,
  PodOrderBatchPrintInwardBody,
  PodOrderBatchPrintOutwardBody,
  PodOrderListingResponse,
  PodOrderMockupsParams,
  PodOrderMockupsResponse,
  PodOrderPrintingFilesBody,
} from "../../../models/PodOrder/PodOrder";
import { APIResponse } from "../../../models/NetworkResponse";
import { toPodPrintingTypeFilterIdentifier } from "../../../models/PodOrder/PodPrintingType";
import { PodOrderProgressStage } from "../../../models/PodOrder/PodOrderProgressStage";

export default class PodOrderAPI {
  getQueryParams = (
    params: GetPodOrdersParams,
    options?: { includePagination?: boolean },
  ): URLSearchParams => {
    const includePagination = options?.includePagination !== false;
    const queryParams = new URLSearchParams();
    if (includePagination) {
      if (params.page) queryParams.append("page_no", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
    }
    if (params.status) queryParams.append("stage", params.status);
    if (params.sku) queryParams.append("sku", params.sku);
    if (params.customer_name)
      queryParams.append("customer_name", params.customer_name);
    if (params.start_date && params.start_date !== "")
      queryParams.append("start_date", params.start_date);
    if (params.end_date && params.end_date !== "")
      queryParams.append("end_date", params.end_date);
    if (params.dispatch_start_date && params.dispatch_start_date !== "")
      queryParams.append("dispatch_start_date", params.dispatch_start_date);
    if (params.dispatch_end_date && params.dispatch_end_date !== "")
      queryParams.append("dispatch_end_date", params.dispatch_end_date);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_type) queryParams.append("sort_type", params.sort_type);
    if (params.order_ids?.length === 1) {
      queryParams.append("order_id", params.order_ids[0].toString());
    } else if (params.order_ids && params.order_ids.length > 1) {
      queryParams.append("order_ids", params.order_ids.join(","));
    }
    if (params.shipment_ids?.length) {
      params.shipment_ids.forEach(id =>
        queryParams.append("shipment_id", id.toString()),
      );
    }
    if (
      params.status === PodOrderProgressStage.UNASSIGNED &&
      (params.previously_allocated === "true" ||
        params.previously_allocated === "false")
    ) {
      queryParams.append("previously_allocated", params.previously_allocated);
    }
    if (params.printing_type) {
      queryParams.append(
        "printing_types",
        toPodPrintingTypeFilterIdentifier(params.printing_type),
      );
    }
    return queryParams;
  };

  getPodOrders = async (params: GetPodOrdersParams): Promise<PodOrderListingResponse> => {
    try {
      const queryParams = this.getQueryParams(params);
      const response = await getAPI(
        `/v1/pod-order/progress?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  bulkDownloadPodOrders = async (
    params: GetPodOrdersParams,
  ): Promise<APIResponse> => {
    const queryParams = this.getQueryParams(params, {
      includePagination: false,
    });
    return await getAPI(
      `/v1/pod-order/progress/bulk-download?${queryParams.toString()}`,
    );
  };

  batchInventoryAllocate = async (
    body: PodOrderBatchInventoryAllocateBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/batch-approve-sir`, body);
    return response;
  };

  setLineWarehouse = async (
    body: PodOrderSetWarehouseBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/set-warehouse`, body);
    return response;
  };

  setPrintingType = async (
    body: PodOrderSetPrintingTypeBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/set-printing-type`, body);
    return response;
  };

  batchUnassign = async (
    body: PodOrderBatchInventoryAllocateBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/batch-unassign`, body);
    return response;
  };

  batchPrintOutward = async (
    body: PodOrderBatchPrintOutwardBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/batch-print-outward`, body);
    return response;
  };

  batchPrintInward = async (
    body: PodOrderBatchPrintInwardBody,
  ): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/batch-print-inward`, body);
    return response;
  };

  uploadPodOrdersCSV = async (file: File): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const formData = new FormData();
      formData.append("csv_file", file);
      return await postMultipartAPI(`/v1/pod-order/import-csv`, formData);
    } catch (error) {
      throw error;
    }
  };

  getPrintingFiles = async (body: PodOrderPrintingFilesBody): Promise<APIResponse> => {
    const response = await postAPI(`/v1/pod-order/printing-files`, body);
    return response;
  };

  getPodOrderMockups = async (
    params: PodOrderMockupsParams,
  ): Promise<PodOrderMockupsResponse> => {
    const response = await getAPI(`/v1/pod-order/mockups`, {
      order_id: params.order_id,
      product_id: params.product_id,
      sku: params.sku,
    });
    return response.result as PodOrderMockupsResponse;
  };
}